/**
 * X25519 Encryption Helper (Hybrid Encryption)
 *
 * Uses @noble/curves and @noble/hashes for key derivation to avoid
 * browser extension interference with WebCrypto importKey.
 *
 * Uses WebCrypto AES-GCM for final encryption (performance).
 */
import { x25519 } from '@noble/curves/ed25519.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { decodeBase64Url, encodeBase64Url } from './codec.js';
const _ALGO_NAME = 'X25519';
export async function encryptWithX25519(data, recipientPublicKeyBase64) {
    // 1. Decode recipient public key
    const recipientPubBytes = decodeBase64Url(recipientPublicKeyBase64);
    // 2. Generate Ephemeral Key Pair (pure JS)
    const ephemeralPriv = x25519.utils.randomSecretKey();
    const ephemeralPub = x25519.getPublicKey(ephemeralPriv);
    // 3. Derive Shared Secret (pure JS)
    const sharedSecret = x25519.getSharedSecret(ephemeralPriv, recipientPubBytes);
    // 4. Derive AES Key via HKDF (pure JS)
    const salt = new Uint8Array(0);
    const info = new TextEncoder().encode('sisumail:x25519:v1');
    const aesKeyBytes = hkdf(sha256, sharedSecret, salt, info, 32); // 32 bytes = 256 bits
    const aesKeyBytesClean = new Uint8Array(aesKeyBytes);
    // 5. Encrypt via WebCrypto AES-GCM (Fast & Standard)
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const dataBuffer = typeof data === 'string' ? new TextEncoder().encode(data) : data;
    const aesKey = await crypto.subtle.importKey('raw', aesKeyBytesClean, { name: 'AES-GCM' }, false, ['encrypt']);
    const ciphertextBuffer = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, dataBuffer);
    const ciphertextBytes = new Uint8Array(ciphertextBuffer);
    // 6. Pack result: [32b Ephemeral Pub] [12b IV] [Ciphertext]
    const result = new Uint8Array(32 + 12 + ciphertextBytes.length);
    result.set(ephemeralPub, 0);
    result.set(iv, 32);
    result.set(ciphertextBytes, 44);
    return encodeBase64Url(result);
}
export async function decryptWithX25519(payloadBase64, privateKeyBase64) {
    const payload = decodeBase64Url(payloadBase64);
    if (payload.length < 44)
        throw new Error('Invalid payload length');
    // Extract
    const ephemeralPubBytes = payload.slice(0, 32);
    const iv = payload.slice(32, 44);
    const ciphertext = payload.slice(44);
    const privKeyBytes = decodeBase64Url(privateKeyBase64);
    // 1. Derive Shared Secret (pure JS)
    const sharedSecret = x25519.getSharedSecret(privKeyBytes, ephemeralPubBytes);
    // 2. Derive AES Key via HKDF (pure JS)
    const salt = new Uint8Array(0);
    const info = new TextEncoder().encode('sisumail:x25519:v1');
    const aesKeyBytes = hkdf(sha256, sharedSecret, salt, info, 32);
    const aesKeyBytesClean = new Uint8Array(aesKeyBytes);
    // 3. Decrypt via WebCrypto AES-GCM
    const aesKey = await crypto.subtle.importKey('raw', aesKeyBytesClean, { name: 'AES-GCM' }, false, ['decrypt']);
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, aesKey, ciphertext);
    return new Uint8Array(decrypted);
}
export async function deriveX25519KeyFromSeed(seed) {
    // X25519 Private Key IS the seed (or scalar derived from it).
    // Noble handles hashing internally if needed, or we just use seed as scalar.
    // For safety, let's just return the seed as Uint8Array.
    return new Uint8Array(seed);
}
export async function generateX25519KeyPair() {
    const priv = x25519.utils.randomSecretKey();
    const pub = x25519.getPublicKey(priv);
    return {
        privateKey: priv,
        publicKey: pub,
    };
}
export async function exportX25519PublicKey(key) {
    // Key is raw bytes
    return encodeBase64Url(key);
}
export async function exportX25519PrivateKeyBytes(key) {
    return key;
}
export async function exportX25519PrivateKey(key) {
    return encodeBase64Url(key);
}
//# sourceMappingURL=x25519.js.map